/**
 * Girth at a named landmark, from mesh vertex positions.
 *
 * Why this exists alongside findNarrowestWaist: the two waists are different
 * measurements, not two implementations of one. A female waist is the
 * narrowest circumference; a male waist is taken over the navel. The narrowest
 * search cannot produce the male figure, and using it anyway would put a
 * narrowest-slice projection beside a navel-height measurement and call both
 * "waist".
 *
 * Pure on purpose — it takes positions rather than a THREE.Mesh — so the
 * arithmetic is unit-testable in the node environment vitest runs in.
 */

export type Vec3 = readonly [number, number, number];

export interface Landmark {
    /** Vertex indices around the circumference. */
    vertices: number[];
    /** Three vertex indices defining the slice plane. */
    plane: [number, number, number];
}

const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: Vec3, b: Vec3): Vec3 => [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
];
const norm = (a: Vec3): Vec3 => {
    const m = Math.sqrt(dot(a, a));
    return m === 0 ? [0, 0, 0] : [a[0] / m, a[1] / m, a[2] / m];
};

/** Monotone chain. The outline is a body cross-section, so it is convex. */
const convexHull = (pts: [number, number][]): [number, number][] => {
    const p = [...new Set(pts.map((q) => `${q[0]},${q[1]}`))]
        .map((s) => s.split(',').map(Number) as [number, number])
        .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    if (p.length < 3) return p;

    const half = (ps: [number, number][]) => {
        const out: [number, number][] = [];
        for (const q of ps) {
            while (
                out.length >= 2 &&
                (out[out.length - 1][0] - out[out.length - 2][0]) *
                    (q[1] - out[out.length - 2][1]) -
                    (out[out.length - 1][1] - out[out.length - 2][1]) *
                        (q[0] - out[out.length - 2][0]) <=
                    0
            ) {
                out.pop();
            }
            out.push(q);
        }
        return out;
    };
    return [...half(p).slice(0, -1), ...half([...p].reverse()).slice(0, -1)];
};

/**
 * Circumference at the landmark, scaled so the mesh stands `heightCm` tall.
 *
 * The mesh arrives in arbitrary units, so it is scaled by its own bounding
 * height against the client's declared height — the same correction
 * measureObj applies.
 */
export const girthFromLandmark = (
    positions: Vec3[],
    landmark: Landmark,
    heightCm: number
): number | null => {
    const { vertices, plane } = landmark;
    if (vertices.length < 3) return null;
    if (plane.some((i) => positions[i] === undefined)) return null;

    const origin = positions[plane[0]];
    const normal = norm(
        cross(sub(positions[plane[1]], origin), sub(positions[plane[2]], origin))
    );

    // An in-plane basis, so the outline can be hulled in two dimensions.
    const ref = norm(sub(positions[plane[1]], origin));
    const along = dot(ref, normal);
    const tangentX = norm([
        ref[0] - along * normal[0],
        ref[1] - along * normal[1],
        ref[2] - along * normal[2],
    ]);
    const tangentY = cross(normal, tangentX);

    const flat: [number, number][] = [];
    for (const i of vertices) {
        const v = positions[i];
        if (!v) continue;
        const rel = sub(v, origin);
        flat.push([dot(rel, tangentX), dot(rel, tangentY)]);
    }
    if (flat.length < 3) return null;

    const hull = convexHull(flat);
    const perimeter = hull.reduce((sum, p, i) => {
        const q = hull[(i + 1) % hull.length];
        return sum + Math.hypot(q[0] - p[0], q[1] - p[1]);
    }, 0);

    const ys = positions.map((p) => p[1]);
    const modelHeight = Math.max(...ys) - Math.min(...ys);
    if (!(modelHeight > 0) || !(heightCm > 0)) return null;

    return perimeter * (heightCm / modelHeight);
};
